import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { PayAsGuestScreenWrapper } from '../../../InvoiceDetails/components/ScreenWrapper'
import InvoiceReceiptWeb from './web/InvoiceReceiptWeb'
import PayAsGuestWeb from './web/PayAsGuestWeb'
import PayAsGuestMobile from './mobile/PayAsGuestMobile'
import InvoiceReceiptMobile from './mobile/InvoiceReceiptMobile'
import { useNavigation } from '@react-navigation/core'
import Button from '../../components/Button'
import { useTranslation } from 'react-i18next'
import { IContactDetails, IPaymentMethodData } from './types'
import SignupStore from '../../../../Auth/stores/SignupStore'
import { SignUpForm } from './auth/SignUpForm'
import { EVENT_TYPE } from '../../../InvoiceDetails/utils/eventTypes'
import { onPaymentSuccess } from '../../hooks/usePaymentStatusHandlers'
import { parsePhoneNumber } from 'libphonenumber-js'

enum Flows {
  PAY_AS_GUEST,
  INVOICE_RECEIPT,
  SIGN_UP,
}

const InvoicePayAsGuestFlow = ({
  invoice,
  isInvoicePaidOff,
  isWeb,
  customer,
  onSignUp,
  mode,
  redirectUrl,
  onUserExists,
}) => {
  window?.opener?.postMessage({ type: EVENT_TYPE.CONNECTION_ESTABLISHED }, '*')
  window?.parent?.postMessage({ type: EVENT_TYPE.CONNECTION_ESTABLISHED }, '*')

  const [currentFlow, setCurrentFlow] = useState<Flows>(Flows.PAY_AS_GUEST)
  const { setValue, setLogin, setPassword, setIsBusiness } = SignupStore
  const [contactDetails, setContactDetails] = useState<IContactDetails>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessName: '',
    isBusiness: true,
  })
  const [paymentMethodData, setPaymentMethodData] =
    useState<IPaymentMethodData | null>(null)

  const navigation = useNavigation() as any
  const { t } = useTranslation('global')

  useEffect(() => {
    setCurrentFlow(
      isInvoicePaidOff ? Flows.INVOICE_RECEIPT : Flows.PAY_AS_GUEST,
    )
  }, [isInvoicePaidOff])

  const handlePaymentSuccess = useCallback(() => {
    console.log('paymentSuccess')

    if (mode === 'popup' || (mode === 'redirect' && redirectUrl)) {
      const currentUrl = window.location.href

      window.open(currentUrl, '_blank')
      onPaymentSuccess({ navigation, mode, redirectUrl })
    } else {
      window.location.reload()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, redirectUrl, isWeb])

  const onCreateAccount = useCallback(
    ({ password }) => {
      let details = contactDetails
      if (!details || Object.values(details).some((value) => value === '')) {
        const storedContactDetails = localStorage.getItem('contactDetails')
        if (storedContactDetails) {
          details = JSON.parse(storedContactDetails)
        }
      }

      setValue('type', 'password')
      setValue('firstName', details?.firstName || '')
      setValue('lastName', details?.lastName || '')
      setValue('email', details?.email || '')
      setValue('phone', parsePhoneNumber(details.phone, 'US').formatNational())
      setValue('company_name', details?.businessName || '')
      setValue('guest_customer_id', invoice.customer_account_id ?? '')
      setLogin(details?.email)

      if (!details?.isBusiness) {
        setIsBusiness()
      }
      setPassword(password)
      setCurrentFlow(Flows.SIGN_UP)
    },
    [contactDetails, setValue, setLogin, setPassword, setIsBusiness, invoice],
  )

  const onBackSignUp = useCallback(() => {
    setCurrentFlow(Flows.INVOICE_RECEIPT)
  }, [])

  const renderWebContent = useMemo(() => {
    if (currentFlow === Flows.PAY_AS_GUEST) {
      return (
        <PayAsGuestWeb
          invoice={invoice}
          paymentMethodData={paymentMethodData}
          setPaymentMethodData={setPaymentMethodData}
          onPaymentSuccess={handlePaymentSuccess}
          contactDetails={contactDetails}
          setContactDetails={setContactDetails}
          customer={customer}
          onUserExists={onUserExists}
        />
      )
    }

    if (currentFlow === Flows.INVOICE_RECEIPT) {
      return (
        <InvoiceReceiptWeb
          invoice={invoice}
          paymentMethodData={paymentMethodData}
          onCreateAccount={onCreateAccount}
        />
      )
    }
    if (currentFlow === Flows.SIGN_UP) {
      return <SignUpForm navigation={navigation} onBack={onBackSignUp} />
    }

    return null
  }, [
    currentFlow,
    invoice,
    onCreateAccount,
    handlePaymentSuccess,
    paymentMethodData,
    setPaymentMethodData,
    contactDetails,
    setContactDetails,
    customer,
    navigation,
    onBackSignUp,
    onUserExists,
  ])

  const renderMobileContent = useMemo(() => {
    if (currentFlow === Flows.PAY_AS_GUEST) {
      return (
        <PayAsGuestMobile
          invoice={invoice}
          paymentMethodData={paymentMethodData}
          setPaymentMethodData={setPaymentMethodData}
          onPaymentSuccess={handlePaymentSuccess}
          contactDetails={contactDetails}
          setContactDetails={setContactDetails}
          customer={customer}
          onSignUp={onSignUp}
          onUserExists={onUserExists}
        />
      )
    }
    if (currentFlow === Flows.INVOICE_RECEIPT) {
      return (
        <InvoiceReceiptMobile
          invoice={invoice}
          paymentMethodData={paymentMethodData}
          onCreateAccount={onCreateAccount}
        />
      )
    }

    if (currentFlow === Flows.SIGN_UP) {
      return <SignUpForm navigation={navigation} onBack={onBackSignUp} />
    }

    return null
  }, [
    currentFlow,
    invoice,
    handlePaymentSuccess,
    paymentMethodData,
    contactDetails,
    setPaymentMethodData,
    setContactDetails,
    onCreateAccount,
    customer,
    navigation,
    onBackSignUp,
    onSignUp,
    onUserExists,
  ])

  const renderButton =
    currentFlow === Flows.PAY_AS_GUEST ? (
      <Button
        label={t('integrations.buttons.sign-up')}
        onPress={onSignUp}
        buttonStyle={{ width: 148, height: 40, backgroundColor: '#F8F9F9' }}
        textStyle={{ fontSize: 14, color: '#001929' }}
      />
    ) : null

  return isWeb ? (
    <PayAsGuestScreenWrapper
      isWeb={isWeb}
      button={renderButton}
      contentViewStyle={{ margin: 0, height: '100%' }}
    >
      {renderWebContent}
    </PayAsGuestScreenWrapper>
  ) : (
    renderMobileContent
  )
}

export default InvoicePayAsGuestFlow
